import React, { useEffect, useState } from "react";
import Menu from "../panel/Menu";
import {
    collection,
    addDoc,
    updateDoc,
    deleteDoc,
    getDocs,
    query,
    where,
    orderBy,
    doc,
    getDoc,
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import {
    Box,
    Paper,
    Typography,
    TextField,
    Button,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Select,
    MenuItem,
    Pagination,
    Chip,
    Divider,
    IconButton,
    Snackbar,
    Alert,
} from "@mui/material";

import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import { db, auth } from "../firebase";

const PAGE_SIZE = 10;

function Roomno() {
    const [userData, setUserData] = useState(null);
    const [propertyId, setPropertyId] = useState(null);

    const [rooms, setRooms] = useState([]);
    const [roomTypes, setRoomTypes] = useState([]);

    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);

    const [form, setForm] = useState({
        roomNo: "",
        status: "Available",
        roomTypeId: "",
        roomTypeName: "",
    });

    const [editId, setEditId] = useState(null);

    /* ---------- TOAST STATE ---------- */
    const [toast, setToast] = useState({
        open: false,
        message: "",
        severity: "success",
    });

    const showToast = (message, severity = "success") => {
        setToast({ open: true, message, severity });
    };

    /* ---------- AUTH ---------- */
    useEffect(() => {
        return onAuthStateChanged(auth, setUserData);
    }, []);

    /* ---------- PROPERTY ---------- */
    useEffect(() => {
        const loadRole = async () => {
            if (!userData) return;
            const snap = await getDoc(doc(db, "authroles", userData.uid));
            if (snap.exists()) setPropertyId(snap.data().propertyId);
        };
        loadRole();
    }, [userData]);

    /* ---------- ROOM TYPES ---------- */
    const fetchRoomTypes = async () => {
        if (!propertyId) return;

        const q = query(
            collection(db, "rooms"),
            where("propertyId", "==", propertyId),
            orderBy("roomTypeName", "asc")
        );

        const snap = await getDocs(q);
        setRoomTypes(
            snap.docs.map(d => ({
                roomTypeId: d.id,
                roomTypeName: d.data().roomTypeName,
            }))
        );
    };

    /* ---------- ROOM NUMBERS ---------- */
    const fetchRooms = async () => {
        if (!propertyId) return;

        const q = query(
            collection(db, "roominfos"),
            where("propertyId", "==", propertyId),
            orderBy("roomNo", "asc")
        );

        const snap = await getDocs(q);
        setRooms(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    };

    useEffect(() => {
        fetchRoomTypes();
        fetchRooms();
    }, [propertyId]);

    /* ---------- SAVE ---------- */
    const saveRoom = async () => {
        if (!form.roomNo || !form.roomTypeId) {
            showToast("Please fill all required fields", "warning");
            return;
        }

        try {
            const payload = {
                ...form,
                userId: userData.uid,
                propertyId,
            };

            if (editId) {
                await updateDoc(doc(db, "roominfos", editId), payload);
                showToast("Room updated successfully");
            } else {
                await addDoc(collection(db, "roominfos"), payload);
                showToast("Room added successfully");
            }

            setForm({ roomNo: "", status: "Available", roomTypeId: "", roomTypeName: "" });
            setEditId(null);
            fetchRooms();

        } catch (err) {
            showToast("Something went wrong", "error");
        }
    };

    /* ---------- EDIT ---------- */
    const editRoom = (room) => {
        setForm(room);
        setEditId(room.id);
    };

    /* ---------- DELETE ---------- */
    const deleteRoom = async (id) => {
        if (!window.confirm("Delete this room?")) return;

        try {
            await deleteDoc(doc(db, "roominfos", id));
            showToast("Room deleted successfully");
            fetchRooms();
        } catch (err) {
            showToast("Delete failed", "error");
        }
    };

    /* ---------- SEARCH + PAGINATION ---------- */
    const filtered = rooms.filter(r =>
        r.roomNo.toLowerCase().includes(search.toLowerCase()) ||
        r.roomTypeName.toLowerCase().includes(search.toLowerCase())
    );

    const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
    const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    return (
        <>
            <Menu />

            <Box className="content-wrapper" sx={{ p: 3, bgcolor: "#f4f6f9", minHeight: "100vh" }}>

                {/* FORM */}
                <Paper sx={{ p: 3, mb: 3, borderRadius: 3 }}>
                    <Typography fontWeight={600} mb={2}>
                        {editId ? "Update Room" : "Add Room"}
                    </Typography>
                    <Divider sx={{ mb: 2 }} />

                    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                        <Select
                            size="small"
                            value={form.roomTypeId}
                            displayEmpty
                            sx={{ minWidth: 200 }}
                            onChange={(e) => {
                                const rt = roomTypes.find(r => r.roomTypeId === e.target.value);
                                setForm({
                                    ...form,
                                    roomTypeId: rt.roomTypeId,
                                    roomTypeName: rt.roomTypeName,
                                });
                            }}
                        >
                            <MenuItem value="">Select Room Type</MenuItem>
                            {roomTypes.map(rt => (
                                <MenuItem key={rt.roomTypeId} value={rt.roomTypeId}>
                                    {rt.roomTypeName}
                                </MenuItem>
                            ))}
                        </Select>

                        <TextField
                            label="Room No"
                            size="small"
                            value={form.roomNo}
                            onChange={(e) => setForm({ ...form, roomNo: e.target.value })}
                        />

                        <Select
                            size="small"
                            value={form.status}
                            onChange={(e) => setForm({ ...form, status: e.target.value })}
                        >
                            <MenuItem value="Available">Available</MenuItem>
                            <MenuItem value="Occupied">Occupied</MenuItem>
                        </Select>

                        <Button variant="contained" onClick={saveRoom}>
                            {editId ? "Update" : "Add"}
                        </Button>
                    </Box>
                </Paper>

                {/* TABLE */}
                <Paper sx={{ p: 3, borderRadius: 3 }}>
                    <TextField
                        size="small"
                        label="Search Room No / Type"
                        value={search}
                        onChange={(e) => {
                            setSearch(e.target.value);
                            setPage(1);
                        }}
                    />
                    <Divider sx={{ my: 2 }} />

                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Sr No</TableCell>
                                <TableCell>Room No</TableCell>
                                <TableCell>Room Type</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="center">Actions</TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {paginated.map((r, index) => (
                                <TableRow key={r.id} hover>

                                    {/* ✅ SR NO */}
                                    <TableCell>
                                        {(page - 1) * PAGE_SIZE + index + 1}
                                    </TableCell>

                                    <TableCell>{r.roomNo}</TableCell>
                                    <TableCell>{r.roomTypeName}</TableCell>

                                    <TableCell>
                                        <Chip
                                            size="small"
                                            label={r.status}
                                            color={r.status === "Available" ? "success" : "error"}
                                        />
                                    </TableCell>

                                    <TableCell align="center">
                                        <IconButton color="primary" onClick={() => editRoom(r)}>
                                            <EditOutlinedIcon />
                                        </IconButton>
                                        <IconButton color="error" onClick={() => deleteRoom(r.id)}>
                                            <DeleteOutlineIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>


                    {totalPages > 1 && (
                        <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
                            <Pagination
                                count={totalPages}
                                page={page}
                                onChange={(e, value) => setPage(value)}
                            />
                        </Box>
                    )}
                </Paper>
            </Box>

            {/* ---------- TOAST ---------- */}
            <Snackbar
                open={toast.open}
                autoHideDuration={3000}
                onClose={() => setToast({ ...toast, open: false })}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
            >
                <Alert severity={toast.severity} variant="filled">
                    {toast.message}
                </Alert>
            </Snackbar>
        </>
    );
}

export default Roomno;
